/**
 * localStorage-backed session storage.
 *
 * Browser localStorage is XSS-readable; this matches Flutter Web's posture
 * (FlutterSecureStorage falls back to sessionStorage on web). Revisit if
 * the backend ever supports HttpOnly refresh-token cookies.
 */

const KEYS = {
  ACCESS_TOKEN: 'scrips.access_token',
  REFRESH_TOKEN: 'scrips.refresh_token',
  USER: 'scrips.user',
  /** Epoch-ms of the last authenticated request — drives the idle-timeout
   *  session cap. Persisted so a returning tab still expires if it was idle
   *  past the limit (Flutter `lastLoginTimeKey`, dio_api_client.dart:294).
   *
   *  Shared-device safety (CoS review PR #670): not tenant-namespaced ON
   *  PURPOSE — it follows the exact same single-session model as the token/
   *  user keys above. `clear()` removes it on logout and `applyTokens` sets it
   *  fresh on login, so it can't bleed across users on a shared device (a
   *  second user logs in → clear() already wiped the first user's value, then
   *  login stamps `now`). Namespacing here without namespacing the tokens it
   *  travels with would add no isolation. */
  LAST_ACTIVITY: 'scrips.last_activity',
} as const;

export const authStorage = {
  getAccessToken: (): string | null => localStorage.getItem(KEYS.ACCESS_TOKEN),
  getRefreshToken: (): string | null => localStorage.getItem(KEYS.REFRESH_TOKEN),

  getUser: <T>(): T | null => {
    const raw = localStorage.getItem(KEYS.USER);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  },

  setSession: (accessToken: string, refreshToken: string, user: unknown): void => {
    localStorage.setItem(KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(KEYS.REFRESH_TOKEN, refreshToken);
    if (user !== null && user !== undefined) {
      localStorage.setItem(KEYS.USER, JSON.stringify(user));
    }
  },

  setTokens: (accessToken: string, refreshToken: string): void => {
    localStorage.setItem(KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(KEYS.REFRESH_TOKEN, refreshToken);
  },

  setUser: (user: unknown): void => {
    if (user === null || user === undefined) return;
    localStorage.setItem(KEYS.USER, JSON.stringify(user));
  },

  getLastActivity: (): number | null => {
    const raw = localStorage.getItem(KEYS.LAST_ACTIVITY);
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  },

  setLastActivity: (epochMs: number): void => {
    localStorage.setItem(KEYS.LAST_ACTIVITY, String(epochMs));
  },

  clear: (): void => {
    localStorage.removeItem(KEYS.ACCESS_TOKEN);
    localStorage.removeItem(KEYS.REFRESH_TOKEN);
    localStorage.removeItem(KEYS.USER);
    localStorage.removeItem(KEYS.LAST_ACTIVITY);
  },
};
